// daemon API 端点常量；新增端点统一在这里登记，避免路径硬编码漂移。
export const API_SESSION = "/api/session";
export const API_AUDIT = "/api/audit";
export const API_TIMELINE = "/api/timeline";
export const API_DASHBOARD = "/api/dashboard";
export const API_ACCOUNTS = "/api/accounts";
export const API_MACHINES = "/api/machines";
export const API_MODULES = "/api/modules";
export const API_MARKET_ANALYSIS = "/api/market-analysis";
export const API_VIP_RANK_AUDIT = "/api/vip-rank-audit";
export const vipRankAuditServerRestartPath = (
  runID: number | string,
  serverID: string,
) =>
  `${API_VIP_RANK_AUDIT}/runs/${encodeURIComponent(String(runID))}/servers/${encodeURIComponent(serverID)}/restart`;
export const vipRankAuditRetryIncompletePath = (runID: number | string) =>
  `${API_VIP_RANK_AUDIT}/runs/${encodeURIComponent(String(runID))}/retry-incomplete`;
export const vipRankAuditRefreshServersPath = (runID: number | string) =>
  `${API_VIP_RANK_AUDIT}/runs/${encodeURIComponent(String(runID))}/refresh-servers`;
export const API_INSTANCES = "/api/instances";
export const API_SETTINGS = "/api/settings";

// 子路径 builders：把动态 id 集中在一处，组件层避免拼字符串。
export const accountDetailPath = (id: number | string) =>
  `${API_ACCOUNTS}/${id}`;
export const accountProfilePath = (id: number | string) =>
  `${API_ACCOUNTS}/${id}/profile`;
export type AccountMarketCDPAction = "status" | "start" | "stop";
export const accountMarketCDPPath = (
  id: number | string,
  action: AccountMarketCDPAction,
) => `${API_ACCOUNTS}/${id}/market-cdp/${action}`;
export const accountDownloadDirProbePath = (id: number | string) =>
  `${API_ACCOUNTS}/${id}/cdp/download-dir/probe`;
export const accountMemberBenefitRefreshPath = (id: number | string) =>
  `${API_ACCOUNTS}/${id}/member-benefit/refresh`;
export type AccountLaunchAction = "refresh-env";
export const accountLaunchPath = (
  id: number | string,
  gameID: string,
  action: AccountLaunchAction,
) =>
  `${API_ACCOUNTS}/${id}/launch/${encodeURIComponent(gameID)}/${action}`;
export const accountModulesPath = (id: number | string) =>
  `${API_ACCOUNTS}/${id}/modules`;
export const accountModuleEditPath = (
  id: number | string,
  module: string,
) => `${API_ACCOUNTS}/${id}/modules/${encodeURIComponent(module)}`;
export const accountModuleResolvePath = (
  id: number | string,
  module: string,
) => `${accountModuleEditPath(id, module)}/resolve`;
export const accountAutoMapHuntUsagePath = (id: number | string) =>
  `${accountModuleEditPath(id, "auto_map_hunt")}/map_usage`;
export const accountAuthMetadataPath = (authID: number | string) =>
  `${API_ACCOUNTS}/auth/${authID}/metadata`;
export const accountAuthDeletePath = (authID: number | string) =>
  `${API_ACCOUNTS}/auth/${authID}/delete`;
export type AuthAction = "verify" | "pending" | "disable" | "revoke";
export const accountAuthActionPath = (
  authID: number | string,
  action: AuthAction,
) => `${API_ACCOUNTS}/auth/${authID}/${action}`;

export const machineDetailPath = (id: number | string) =>
  `${API_MACHINES}/${id}`;
export const machineCachesPath = (id: number | string) =>
  `${API_MACHINES}/${id}/caches`;
export const machineMarketCDPPath = (id: number | string) =>
  `${API_MACHINES}/${id}/market-cdp`;
export const machineAuthMetadataPath = (authID: number | string) =>
  `${API_MACHINES}/auth/${authID}/metadata`;
export const machineAuthDeletePath = (authID: number | string) =>
  `${API_MACHINES}/auth/${authID}/delete`;
export const machineAuthActionPath = (
  authID: number | string,
  action: AuthAction,
) => `${API_MACHINES}/auth/${authID}/${action}`;

export const instanceDetailPath = (id: string) =>
  `${API_INSTANCES}/${encodeURIComponent(id)}`;
export const instanceForceReloadPath = (id: string) =>
  `${API_INSTANCES}/${encodeURIComponent(id)}/force-reload`;
export const instanceRestartGamePath = (id: string) =>
  `${API_INSTANCES}/${encodeURIComponent(id)}/restart-game`;
export const instanceDeletePath = (id: string) =>
  `${API_INSTANCES}/${encodeURIComponent(id)}/delete`;
export const instanceSetTargetServerPath = (id: string) =>
  `${API_INSTANCES}/${encodeURIComponent(id)}/set-target-server`;
export const instanceClearTargetServerPath = (id: string) =>
  `${API_INSTANCES}/${encodeURIComponent(id)}/set-target-server?clear=1`;
export const instanceModuleEditPath = (id: string, module: string) =>
  `${API_INSTANCES}/${encodeURIComponent(id)}/modules/${encodeURIComponent(module)}`;
export const instanceAutoMapHuntObservabilityPath = (id: string) =>
  `${API_INSTANCES}/${encodeURIComponent(id)}/auto-map-hunt/observability`;
export const instanceProfitPath = (
  id: string,
  window = "60m",
  groupBy: "none" | "map" | "item" = "map",
  includeExcluded = false,
) =>
  `${API_INSTANCES}/${encodeURIComponent(id)}/profit?window=${encodeURIComponent(window)}&group_by=${encodeURIComponent(groupBy)}${includeExcluded ? "&include_excluded=1" : ""}`;

export const agentCommandPath = (agentID: string) =>
  `/api/agents/${agentID}/command`;
export const agentSingBoxApplyPath = (agentID: string) =>
  `/api/agents/${encodeURIComponent(agentID)}/sing-box/apply`;
export const agentSingBoxAutoAssignPath = (agentID: string) =>
  `/api/agents/${encodeURIComponent(agentID)}/sing-box/auto-assign`;
export const agentSingBoxStatePath = (agentID: string) =>
  `/api/agents/${encodeURIComponent(agentID)}/sing-box/state`;
export const agentSingBoxPlanPath = (agentID: string) =>
  `/api/agents/${encodeURIComponent(agentID)}/sing-box/plan`;
export const agentSingBoxHistoryPath = (agentID: string) =>
  `/api/agents/${encodeURIComponent(agentID)}/sing-box/history`;
export const agentSingBoxVersionsPath = (agentID: string) =>
  `/api/agents/${encodeURIComponent(agentID)}/sing-box/versions`;
export const agentSingBoxRollbackPath = (agentID: string) =>
  `/api/agents/${encodeURIComponent(agentID)}/sing-box/rollback`;
export const agentLaunchCapacityPath = (agentID: string) =>
  `/api/agents/${encodeURIComponent(agentID)}/capacity`;
export const launchSlotsPath = (
  agentIDOrOptions?: string | { agentID?: string; accountID?: number | string },
) => {
  const params = new URLSearchParams();
  if (typeof agentIDOrOptions === "string") {
    if (agentIDOrOptions) {
      params.set("agent_id", agentIDOrOptions);
    }
  } else if (agentIDOrOptions) {
    if (agentIDOrOptions.agentID) {
      params.set("agent_id", agentIDOrOptions.agentID);
    }
    if (agentIDOrOptions.accountID !== undefined && agentIDOrOptions.accountID !== "") {
      params.set("account_id", String(agentIDOrOptions.accountID));
    }
  }
  const query = params.toString();
  return `/api/launch-slots${query ? `?${query}` : ""}`;
};
export const launchSlotPath = (id: number | string) =>
  `/api/launch-slots/${encodeURIComponent(String(id))}`;
export const launchSlotRefreshStatusPath = (id: number | string) =>
  `${launchSlotPath(id)}/refresh-status`;
export const launchSlotProbeExitIPPath = (id: number | string) =>
  `${launchSlotPath(id)}/probe-exit-ip`;
export const gameClientCapacitiesPath = "/api/game-client-capacities";
export const gameClientCapacityPath = (gameID: string) =>
  `/api/game-client-capacities/${encodeURIComponent(gameID)}`;
export const launchSlotGameCapacitiesPath = (slotID: number | string) =>
  `/api/launch-slots/${encodeURIComponent(String(slotID))}/game-capacity`;
export const launchSlotGameCapacityPath = (
  slotID: number | string,
  gameID: string,
) =>
  `/api/launch-slots/${encodeURIComponent(String(slotID))}/game-capacity/${encodeURIComponent(gameID)}`;
export const proxyProfilesPath = "/api/proxy-profiles";
export const proxyProfilesQueryPath = (filters: {
  q?: string;
  state?: string;
  health?: string;
}) => {
  const params = new URLSearchParams();
  if (filters.q?.trim()) params.set("q", filters.q.trim());
  if (filters.state && filters.state !== "all") params.set("state", filters.state);
  if (filters.health && filters.health !== "all") params.set("health", filters.health);
  const query = params.toString();
  return query ? `${proxyProfilesPath}?${query}` : proxyProfilesPath;
};
export const proxyProfilesImportPath = "/api/proxy-profiles/import";
export const proxyProfilePath = (id: number | string) =>
  `/api/proxy-profiles/${encodeURIComponent(String(id))}`;
export const proxyProfileProbePath = (id: number | string) =>
  `${proxyProfilePath(id)}/probe`;
export const roleLaunchBindingsPath = (
  slotIDOrOptions?:
    | number
    | string
    | { slotID?: number | string; accountID?: number | string },
) => {
  const params = new URLSearchParams();
  if (
    typeof slotIDOrOptions === "number" ||
    typeof slotIDOrOptions === "string"
  ) {
    params.set("slot_id", String(slotIDOrOptions));
  } else if (slotIDOrOptions) {
    if (slotIDOrOptions.slotID !== undefined) {
      params.set("slot_id", String(slotIDOrOptions.slotID));
    }
    if (slotIDOrOptions.accountID !== undefined) {
      params.set("account_id", String(slotIDOrOptions.accountID));
    }
  }
  const query = params.toString();
  return `/api/role-launch-bindings${query ? `?${query}` : ""}`;
};
export const roleLaunchBindingPath = (id: number | string) =>
  `/api/role-launch-bindings/${encodeURIComponent(String(id))}`;
export const roleLaunchBindingStartPath = (id: number | string) =>
  `${roleLaunchBindingPath(id)}/start`;
export const roleLaunchBindingSyncEnvPath = (id: number | string) =>
  `${roleLaunchBindingPath(id)}/sync-env`;
export const roleLaunchBindingRefreshEnvAndStartPath = (id: number | string) =>
  `${roleLaunchBindingPath(id)}/refresh-env-and-start`;
export const roleLaunchBindingStopPath = (id: number | string) =>
  `${roleLaunchBindingPath(id)}/stop`;
export const roleLaunchBindingRestartPath = (id: number | string) =>
  `${roleLaunchBindingPath(id)}/restart`;
export const launchJobPath = (id: number | string) =>
  `/api/launch-jobs/${encodeURIComponent(String(id))}`;

export const modulesBatchCommandPath = "/api/modules/batch-command";
export const modulesAccountMatrixPath = "/api/modules/account-matrix";
export const moduleJobPath = (jobID: string) => `/api/module-jobs/${jobID}`;
export const jobPath = (jobID: string) => `/api/jobs/${jobID}`;

export const cachePolicyBatchApplyPath =
  "/api/machines/cache-policy/batch-apply";
export const cachePolicyPath = (cacheID: number | string) =>
  `${API_MACHINES}/cache-policy/${cacheID}`;
export const cachePolicyApplyPath = (cacheID: number | string) =>
  `${cachePolicyPath(cacheID)}/apply`;
export const cachePolicyClearPath = (cacheID: number | string) =>
  `${cachePolicyPath(cacheID)}/clear`;
export const confirmNoncePath = "/api/confirm/nonce";
export const launcherPolicyPath = `${API_SETTINGS}/launcher-policy`;

// game profile API（Phase 4）。worktree 命名风格 — `apiXxx(id)` 直接返回完整路径。
export const API_GAMES = "/api/games";
export const apiGameBundle = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/bundle`;
export const apiGameUpsert = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}`;
export const apiGameJSONField = (
  gameID: string,
  field: "global" | "protect" | "patrol_templates" | "assisted_cruise_random_fly",
) => `/api/games/${encodeURIComponent(gameID)}/${field}`;
export const apiGameModules = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/modules`;
export const apiGameModule = (gameID: string, module: string) =>
  `${apiGameModules(gameID)}/${encodeURIComponent(module)}`;
export const apiGameModuleClear = (gameID: string, module: string) =>
  `${apiGameModule(gameID, module)}/clear`;
export const apiGameModuleReferences = (gameID: string, module: string) =>
  `${apiGameModule(gameID, module)}/references`;
export const apiContinents = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/continents`;
export const apiContinentDelete = (gameID: string, continentID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/continents/${encodeURIComponent(continentID)}/delete`;
export const apiMaps = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/maps`;
export const apiMapItem = (gameID: string, mapID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/maps/${encodeURIComponent(mapID)}`;
export const apiServerGroups = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/server_groups`;
export const apiServerGroupMember = (gameID: string, memberID: string) =>
  `${apiServerGroups(gameID)}/${encodeURIComponent(memberID)}`;
export const apiServerGroupRename = (gameID: string, canonicalID: string) =>
  `${apiServerGroups(gameID)}/${encodeURIComponent(canonicalID)}/rename`;
export const apiGameServers = (gameID: string, query = "", limit = 10) =>
  `/api/games/${encodeURIComponent(gameID)}/servers?q=${encodeURIComponent(query)}&limit=${encodeURIComponent(String(limit))}`;
export const apiCanonicalGameServers = (gameID: string, query = "", limit = 10) =>
  `/api/games/${encodeURIComponent(gameID)}/servers?q=${encodeURIComponent(query)}&limit=${encodeURIComponent(String(limit))}&canonical=1`;
export const apiMapRespawnStats = (gameID: string, serverID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/map_respawn_stats?server_id=${encodeURIComponent(serverID)}`;
export const apiMapRespawnTargets = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/map_respawn_targets`;
export const apiAutoMapHuntLeases = (gameID: string, serverID = "") =>
  `/api/games/${encodeURIComponent(gameID)}/auto_map_hunt/leases${serverID ? `?server_id=${encodeURIComponent(serverID)}` : ""}`;
export const apiAutoMapHuntLeaseRelease = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/auto_map_hunt/leases/release`;
export const apiProfitValueRules = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/profit/value-rules`;
export const apiProfitSourceRules = (gameID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/profit/source-rules`;
export const apiGameProfitInstances = (
  gameID: string,
  window = "60m",
  serverID = "",
  limit = 20,
) =>
  `/api/games/${encodeURIComponent(gameID)}/profit/instances?window=${encodeURIComponent(window)}&limit=${encodeURIComponent(String(limit))}${serverID ? `&server_id=${encodeURIComponent(serverID)}` : ""}`;
export const apiGameProfitMaps = (
  gameID: string,
  window = "60m",
  serverID = "",
  limit = 20,
) =>
  `/api/games/${encodeURIComponent(gameID)}/profit/maps?window=${encodeURIComponent(window)}&limit=${encodeURIComponent(String(limit))}${serverID ? `&server_id=${encodeURIComponent(serverID)}` : ""}`;
export const apiGameProfitItems = (
  gameID: string,
  window = "60m",
  serverID = "",
  limit = 20,
) =>
  `/api/games/${encodeURIComponent(gameID)}/profit/items?window=${encodeURIComponent(window)}&limit=${encodeURIComponent(String(limit))}${serverID ? `&server_id=${encodeURIComponent(serverID)}` : ""}`;
export const apiMapDelete = (gameID: string, mapID: string) =>
  `/api/games/${encodeURIComponent(gameID)}/maps/${encodeURIComponent(mapID)}/delete`;
export const apiMaterials = "/api/materials";
export const apiMaterialItem = (id: number | string) =>
  `/api/materials/${id}`;
export const apiMaterialDelete = (id: number | string) =>
  `/api/materials/${id}/delete`;
export const apiCurrencies = "/api/currencies";
export const apiCurrencyItem = (id: number | string) =>
  `/api/currencies/${id}`;
export const apiCurrencyDelete = (id: number | string) =>
  `/api/currencies/${id}/delete`;
